import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';
import { uploadPublicImage } from '../services/publicMediaUpload';
import AuthorSearchInput from '../components/AuthorSearchInput';
import DailyBookCounts from '../components/DailyBookCounts';
import { CanonicalPicker, type Resolution } from '../components/CanonicalResolution';

// ── Types ────────────────────────────────────────────────────────────
interface Book {
  is_translation?: boolean;
  original_author?: string | null;
  author_id?: string | null;
  publisher_id?: string | null;
  id: string;
  title: string;
  english_name: string | null;
  author: string | null;
  author_display?: string | null;
  translator_display?: string | null;
  publisher: string | null;
  page_count: number | null;
  genre: string | null;
  language: string | null;
  price_lkr: number | null;
  description: string | null;
  cover_url: string | null;
  is_active: boolean;
  canonical_book_id: string | null;
  canonical_title: string | null;
  library_count: number;
  isbn13: string | null;
}

interface BooksResponse {
  books: Book[];
  total: number;
  page: number;
  pages: number;
}

interface BookRefs {
  library_count: number;
  wisdom_posts_count: number;
  price_history_count: number;
  series_books_count: number;
}

interface QualityReport {
  total_books: number;
  active_books: number;
  inactive_books: number;
  no_cover: number;
  no_isbn: number;
  no_author: number;
  orphan_books: number;
  marked_as_duplicate: number;
  duplicate_groups: number;
  no_price: number;
  no_publisher: number;
  no_description: number;
  translation_count: number;
}

const qualityMetricKeys: (keyof QualityReport)[] = [
  'total_books', 'active_books', 'inactive_books', 'no_cover', 'no_isbn', 'no_author',
  'orphan_books', 'marked_as_duplicate', 'duplicate_groups', 'no_price',
  'no_publisher', 'no_description', 'translation_count',
];

interface DuplicateBook {
  id: string;
  title: string;
  author: string | null;
  cover_url: string | null;
  library_count: number;
  created_at: string;
}

interface DuplicateGroup {
  title: string;
  author: string | null;
  books: DuplicateBook[];
}

interface DuplicateScanResult {
  title_duplicates: DuplicateGroup[];
  isbn_duplicates: DuplicateGroup[];
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const savedIsbnSourceLabels = {
  book: 'Main catalogue',
  linked_sinhala: 'Linked Sinhala catalogue',
  legacy_identifier: 'Legacy saved ISBN',
};
interface SavedIsbn {
  source: keyof typeof savedIsbnSourceLabels;
  isbn: string;
}
interface IsbnRepairPreview {
  book: { id: string; title: string; author: string | null; isbn13: string | null };
  library_count: number;
  linked_sinhala_book: { id: string; title: string; isbn: string } | null;
  matching_sinhala_book: { id: string; title: string; isbn: string } | null;
  suggested_isbn: string | null;
  stored_isbns: SavedIsbn[];
  candidate_isbn13: string | null;
  checksum_valid?: boolean;
  conflict_book: { id: string; title: string } | null;
  can_apply: boolean;
  reason: string | null;
}

type TabId = 'all' | 'duplicates' | 'quality';
type FilterValue = 'all' | 'inactive' | 'no_cover' | 'no_isbn' | 'no_author' | 'orphan' | 'duplicates_only' | 'no_price' | 'no_publisher' | 'no_description' | 'translation';

// ── Toast Component ──────────────────────────────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          toast-enter cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}
  </div>
);

// ── Confirm Dialog ───────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">{message}</p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all ${confirmColor} disabled:opacity-50 active:scale-95`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Skeleton Row ─────────────────────────────────────────────────────
const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 6 }).map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded-lg w-3/4" />
      </td>
    ))}
  </tr>
);

// ── Spinner ──────────────────────────────────────────────────────────
const Spinner: React.FC<{ size?: string }> = ({ size = 'h-5 w-5' }) => (
  <svg className={`animate-spin ${size}`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Cover Thumbnail ──────────────────────────────────────────────────
const CoverThumb: React.FC<{ url: string | null; title: string }> = ({ url, title }) => {
  if (url) {
    return (
      <img
        src={url}
        alt={title}
        className="w-8 h-12 object-cover rounded-md shadow-sm shrink-0"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-8 h-12 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
      <span className="text-gray-400 text-xs">📖</span>
    </div>
  );
};

// ── Main BooksPage Component ─────────────────────────────────────────
const BooksPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [dailyRefresh, setDailyRefresh] = useState(0);

  // ── Toast helpers ────────────────────────────────────────────────
  const toastIdRef = useRef(0);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Quality report (fetched once on mount for stats bar) ─────────
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualityError, setQualityError] = useState<string | null>(null);
  const qualityRequest = useRef(0);
  const qualityMounted = useRef(false);
  const invalidateQualityRequests = useCallback(() => { qualityRequest.current++; }, []);

  const fetchQualityReport = useCallback(async () => {
    if (!qualityMounted.current) return;
    const request = ++qualityRequest.current;
    const isCurrent = () => qualityMounted.current && request === qualityRequest.current;
    setQualityLoading(true);
    setQualityReport(null); setQualityError(null);
    try {
      const res = await adminApi.get<QualityReport>('/admin/books/quality/report');
      if (!isCurrent()) return;
      if (!res.data || !qualityMetricKeys.every(key => Number.isSafeInteger(res.data[key]) && res.data[key] >= 0)) {
        throw new Error('Incomplete quality report');
      }
      setQualityReport(res.data);
    } catch (err: any) {
      if (!isCurrent()) return;
      const message = err.response?.status === 403 ? 'Admin access required'
        : 'Quality report unavailable. Retry to load current counts.';
      setQualityReport(null); setQualityError(message);
      addToast(message, 'error');
    } finally {
      if (isCurrent()) setQualityLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    qualityMounted.current = true;
    fetchQualityReport();
    return () => { qualityMounted.current = false; invalidateQualityRequests(); };
  }, [fetchQualityReport, invalidateQualityRequests]);

  // ─────────────────────────────────────────────────────────────────
  // TAB 1: All Books state
  // ─────────────────────────────────────────────────────────────────
  const [books, setBooks] = useState<Book[]>([]);
  const [booksTotal, setBooksTotal] = useState(0);
  const [booksPage, setBooksPage] = useState(1);
  const [booksTotalPages, setBooksTotalPages] = useState(1);
  const [booksSearch, setBooksSearch] = useState('');
  const [booksFilter, setBooksFilter] = useState<FilterValue>('all');
  const [booksLoading, setBooksLoading] = useState(true);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const perPage = 20;

  const fetchBooks = useCallback(async (pg: number, query: string, filter: FilterValue) => {
    setBooksLoading(true);
    try {
      const params: Record<string, any> = { page: pg, limit: perPage };
      if (query.trim()) params.search = query.trim();
      if (filter !== 'all') params.filter = filter;
      const res = await adminApi.get<BooksResponse>('/admin/books', { params });
      setBooks(res.data.books);
      setBooksTotal(res.data.total);
      setBooksTotalPages(res.data.pages ?? Math.ceil(res.data.total / perPage));
    } catch (err: any) {
      if (err.response?.status === 403) {
        addToast('Admin access required', 'error');
      } else {
        addToast('Failed to load books.', 'error');
      }
    } finally {
      setBooksLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchBooks(booksPage, booksSearch, booksFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booksPage, activeTab]);

  const handleFilterChange = (val: FilterValue) => {
    setBooksFilter(val);
    setBooksPage(1);
    fetchBooks(1, booksSearch, val);
  };

  const handleSearchChange = (value: string) => {
    setBooksSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setBooksPage(1);
      fetchBooks(1, value, booksFilter);
    }, 400);
  };

  // ── Tab switching ─────────────────────────────────────────────────
  const switchTab = (tab: TabId, filter?: FilterValue) => {
    setActiveTab(tab);
    if (tab === 'quality') {
      fetchQualityReport();
    }
    if (tab === 'all' && filter) {
      setBooksFilter(filter);
      setBooksPage(1);
      fetchBooks(1, booksSearch, filter);
    }
  };

  const [repairBook, setRepairBook] = useState<Book | null>(null);
  const [repairIsbn, setRepairIsbn] = useState('');
  const [repairNote, setRepairNote] = useState('');
  const [repairPreview, setRepairPreview] = useState<IsbnRepairPreview | null>(null);
  const [repairVerified, setRepairVerified] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const repairRequestRef = useRef(0);
  const [repairSaved, setRepairSaved] = useState<{ status: 'loading' | 'loaded' | 'error'; items: SavedIsbn[] }>({ status: 'loading', items: [] });
  const [repairSavedRetry, setRepairSavedRetry] = useState(0);
  const repairBookId = repairBook?.id;

  useEffect(() => {
    if (!repairBookId) return;
    let active = true;
    setRepairSaved({ status: 'loading', items: [] });
    adminApi.get<IsbnRepairPreview>(`/admin/books/${repairBookId}/isbn-repair/preview`, { params: { isbn: '' } })
      .then(({ data }) => {
        if (data?.book?.id !== repairBookId || !Array.isArray(data.stored_isbns) || data.stored_isbns.some(item =>
          !item || !Object.prototype.hasOwnProperty.call(savedIsbnSourceLabels, item.source) ||
          typeof item.isbn !== 'string' || !item.isbn.trim())) throw new Error('Invalid saved ISBN response');
        if (active) setRepairSaved({ status: 'loaded', items: data.stored_isbns });
      })
      .catch(() => { if (active) setRepairSaved({ status: 'error', items: [] }); });
    return () => { active = false; };
  }, [repairBookId, repairSavedRetry]);
  useEffect(() => () => { repairRequestRef.current += 1; }, [repairBookId]);

  const openRepairModal = (book: Book) => {
    repairRequestRef.current += 1;
    setRepairBook(book);
    setRepairIsbn('');
    setRepairNote('');
    setRepairPreview(null);
    setRepairVerified(false);
    setRepairLoading(false);
    setRepairSaved({ status: 'loading', items: [] });
    setRepairSavedRetry(0);
  };

  const changeRepairIsbn = (value: string) => {
    repairRequestRef.current += 1;
    setRepairLoading(false);
    setRepairIsbn(value);
    setRepairPreview(null);
    setRepairVerified(false);
  };

  const checkRepairIsbn = async () => {
    if (!repairBook) return;
    const requestId = ++repairRequestRef.current;
    setRepairLoading(true);
    setRepairVerified(false);
    try {
      const res = await adminApi.get<IsbnRepairPreview>(`/admin/books/${repairBook.id}/isbn-repair/preview`, {
        params: { isbn: repairIsbn.trim() },
      });
      if (requestId === repairRequestRef.current) setRepairPreview(res.data);
    } catch (err: any) {
      if (requestId === repairRequestRef.current) {
        setRepairPreview(null);
        addToast(err.response?.data?.detail || 'ISBN check failed.', 'error');
      }
    } finally {
      if (requestId === repairRequestRef.current) setRepairLoading(false);
    }
  };

  const applyRepairIsbn = async () => {
    if (!repairBook || repairPreview?.book.id !== repairBook.id || !repairPreview.can_apply || !repairPreview.candidate_isbn13 || !repairVerified) return;
    setRepairLoading(true);
    try {
      await adminApi.post(`/admin/books/${repairBook.id}/isbn-repair`, {
        isbn: repairIsbn.trim(),
        expected_isbn13: repairPreview.candidate_isbn13,
        physical_book_verified: true,
        note: repairNote.trim(),
      });
      addToast('ISBN attached to the existing book.', 'success');
      setDailyRefresh(value => value + 1);
      setRepairBook(null);
      setRepairPreview(null);
      if (booksFilter === 'no_isbn') setBooksPage(1);
      fetchBooks(booksFilter === 'no_isbn' ? 1 : booksPage, booksSearch, booksFilter);
      fetchQualityReport();
    } catch (err: any) {
      setRepairVerified(false);
      addToast(err.response?.data?.detail || 'ISBN repair failed. Check again.', 'error');
    } finally {
      setRepairLoading(false);
    }
  };

  // ── Edit Modal ───────────────────────────────────────────────────
  const [editModal, setEditModal] = useState<{ open: boolean; book: Book | null; loading?: boolean; error?: string }>({ open: false, book: null });
  const editDetailsRequest = useRef(0);
  const [editForm, setEditForm] = useState<Partial<Book> & Resolution>({});
  const [editLoading, setEditLoading] = useState(false);
  const [coverUploadLoading, setCoverUploadLoading] = useState(false);

  // Translation payload is separate; both original-author pickers share this draft.
  const [translationSectionOpen, setTranslationSectionOpen] = useState(false);
  const [translationForm, setTranslationForm] = useState({
    is_translation: false,
    original_title: '',
    original_author: '',
    author_id: null as string | null,
    translator: '',
    translator_id: null as string | null,
  });
  const [translationSaving, setTranslationSaving] = useState(false);

  const selectOriginalAuthor = (id: string | null, name: string) => {
    setTranslationForm(previous => ({ ...previous, original_author: name, author_id: id }));
    if (editModal.book?.is_translation) {
      setEditForm(previous => ({ ...previous, original_author: name,
        author_ref: id ? { id, role: 'original_author' } : undefined }));
    }
  };

  useEffect(() => () => { editDetailsRequest.current += 1; }, []);

  const closeEditModal = () => {
    editDetailsRequest.current += 1;
    setEditModal({ open: false, book: null });
    setEditForm({});
  };

  const coverUploadInFlight = useRef(false);
  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (coverUploadInFlight.current || !e.target.files || !e.target.files[0] || !editModal.book || editModal.loading || editModal.error) return;
    coverUploadInFlight.current = true;
    const file = e.target.files[0];

    setCoverUploadLoading(true);
    try {
      const secureUrl = await uploadPublicImage(file, 'books');

      await adminApi.patch(`/admin/books/${editModal.book.id}/cover`, { cover_url: secureUrl });

      setEditModal(prev => prev.book ? { ...prev, book: { ...prev.book, cover_url: secureUrl } } : prev);
      setEditForm(prev => ({ ...prev, cover_url: secureUrl }));
      addToast('Cover updated', 'success');
      fetchBooks(booksPage, booksSearch, booksFilter);
    } catch (err) {
      console.error('Cover upload failed');
      addToast('Failed to update cover', 'error');
    } finally {
      coverUploadInFlight.current = false;
      setCoverUploadLoading(false);
      e.target.value = '';
    }
  };

  const openEditModal = async (book: Book) => {
    const requestId = ++editDetailsRequest.current;
    setEditModal({ open: true, book, loading: true });
    setEditForm({});
    // Reset translation section on each open — collapsed by default
    setTranslationSectionOpen(false);
    setTranslationForm({
      is_translation: false,
      original_title: '',
      original_author: '',
      author_id: null,
      translator: '',
      translator_id: null,
    });
    try {
      const res = await adminApi.get<any>(`/admin/books/${book.id}`);
      if (requestId !== editDetailsRequest.current) return;
      const full = res.data;
      // The list response is intentionally incomplete and must never become an edit form.
      const editFields = ['title', 'english_name', 'author', 'publisher', 'page_count',
        'genre', 'language', 'price_lkr', 'description'];
      if (!full || full.id !== book.id || editFields.some(field =>
        !Object.prototype.hasOwnProperty.call(full, field))) {
        throw new Error('Incomplete book details');
      }
      setEditForm({
        title: full.title,
        english_name: full.english_name ?? '',
        author: full.author ?? '',
        publisher: full.publisher ?? '',
        page_count: full.page_count ?? undefined,
        genre: full.genre ?? '',
        language: full.language ?? '',
        price_lkr: full.price_lkr ?? undefined,
        description: full.description ?? '',
      });
      // Pre-populate translation fields if book already has translation data
      if (full.is_translation) {
        setTranslationForm({
          is_translation: !!full.is_translation,
          original_title: full.original_title ?? '',
          original_author: full.original_author ?? '',
          author_id: full.author_id ?? null,
          translator: full.translator ?? '',
          translator_id: full.translator_id ?? null,
        });
        setTranslationSectionOpen(true);
      }
      setEditModal({ open: true, book: full as Book, loading: false });
    } catch (err: any) {
      if (requestId !== editDetailsRequest.current) return;
      setEditModal({
        open: true, book, loading: false,
        error: typeof err.response?.data?.detail === 'string' && err.response.data.detail.trim()
          ? err.response.data.detail : 'Book details could not be loaded. Retry before editing.',
      });
    }
  };

  const handleEditSave = async () => {
    if (!editModal.book || editModal.loading || editModal.error || editLoading) return;
    setEditLoading(true);
    try {
      await adminApi.patch(`/admin/books/${editModal.book.id}`, editForm);
      addToast('Book updated successfully.', 'success');
      closeEditModal();
      fetchBooks(booksPage, booksSearch, booksFilter);
    } catch (err: any) {
      addToast(err.response?.data?.detail || (err.response?.status === 403
        ? 'Admin access required' : 'Failed to update book.'), 'error');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Translation Save Handler (separate endpoint, separate payload) ──
  const handleTranslationSave = async () => {
    if (!editModal.book || editModal.loading || editModal.error || translationSaving) return;
    const requestId = editDetailsRequest.current;
    const submitted = translationForm;
    const submittedBook = editModal.book;
    setTranslationSaving(true);
    try {
      // NOTE: BooksPage currently only surfaces the 'books' table.
      // sinhala_books translation edits will need a separate surface once
      // sinhala_books rows are exposed here.
      const response = await adminApi.patch(`/admin/master/books/${editModal.book.id}/translation`, {
        source_table: 'books',
        is_translation: translationForm.is_translation,
        original_title: translationForm.original_title || null,
        original_author: translationForm.original_author || null,
        translator: translationForm.translator || null,
        author_id: translationForm.author_id || null,
        translator_id: translationForm.translator_id || null,
      });
      if (requestId === editDetailsRequest.current) {
        const saved = response.data;
        const confirmed = {
          is_translation: saved.is_translation ?? submitted.is_translation,
          original_title: saved.original_title === undefined ? submitted.original_title : saved.original_title ?? '',
          original_author: saved.original_author === undefined ? submitted.original_author : saved.original_author ?? '',
          author_id: saved.author_id === undefined ? submitted.author_id : saved.author_id,
          translator: saved.translator === undefined ? submitted.translator : saved.translator ?? '',
          translator_id: saved.translator_id === undefined ? submitted.translator_id : saved.translator_id,
        };
        setEditModal(previous => previous.book ? { ...previous, book: { ...previous.book, ...saved } } : previous);
        setTranslationForm(current => {
          const next = { ...current };
          if (current.is_translation === submitted.is_translation) next.is_translation = confirmed.is_translation;
          if (current.original_title === submitted.original_title) next.original_title = confirmed.original_title;
          if (current.is_translation === submitted.is_translation && current.original_author === submitted.original_author && current.author_id === submitted.author_id) {
            next.original_author = confirmed.original_author; next.author_id = confirmed.author_id;
          }
          if (current.translator === submitted.translator && current.translator_id === submitted.translator_id) {
            next.translator = confirmed.translator; next.translator_id = confirmed.translator_id;
          }
          return next;
        });
        setEditForm(current => {
          const remaining = { ...current };
          if (confirmed.is_translation !== !!submittedBook.is_translation) {
            // Changing the saved role invalidates the old role's author draft.
            delete remaining.author_ref; delete remaining.original_author;
            remaining.author = saved.author === undefined ? submittedBook.author ?? '' : saved.author ?? '';
            return remaining;
          }
          if (!confirmed.is_translation && saved.author !== undefined &&
              current.author === submittedBook.author && !Object.prototype.hasOwnProperty.call(current, 'author_ref')) {
            remaining.author = saved.author ?? '';
          }
          // Remove only the submitted original-author edit. Later edits and
          // unrelated general/publisher drafts must survive this confirmation.
          if (current.original_author !== submitted.original_author ||
              (current.author_ref?.id || null) !== submitted.author_id ||
              (current.author_ref && current.author_ref.role !== 'original_author')) return remaining;
          delete remaining.author_ref; delete remaining.original_author;
          return remaining;
        });
      }
      addToast('Translation details saved.', 'success');
      // Refresh the books list so any filter badges update
      fetchBooks(booksPage, booksSearch, booksFilter);
      fetchQualityReport();
    } catch (err: any) {
      // Surface the backend's validation message directly (400 for bad author_id/translator_id, etc.)
      addToast(err.response?.data?.detail || 'Failed to save translation details.', 'error');
    } finally {
      setTranslationSaving(false);
    }
  };

  // ── Refs Modal ───────────────────────────────────────────────────
  const [refsModal, setRefsModal] = useState<{
    open: boolean; bookId: string; bookTitle: string;
    data: BookRefs | null; loading: boolean;
  }>({ open: false, bookId: '', bookTitle: '', data: null, loading: false });

  const openRefsModal = async (book: Book) => {
    setRefsModal({ open: true, bookId: book.id, bookTitle: book.title, data: null, loading: true });
    try {
      const res = await adminApi.get<BookRefs>(`/admin/books/${book.id}/references`);
      setRefsModal((prev) => ({ ...prev, data: res.data, loading: false }));
    } catch (err: any) {
      if (err.response?.status === 403) {
        addToast('Admin access required', 'error');
      } else {
        addToast('Failed to load references.', 'error');
      }
      setRefsModal((prev) => ({ ...prev, open: false, loading: false }));
    }
  };

  // ── Mark Duplicate Modal ─────────────────────────────────────────
  const [markDupModal, setMarkDupModal] = useState<{
    open: boolean; book: Book | null; search: string;
    results: Book[]; searching: boolean; selected: Book | null; confirming: boolean;
  }>({ open: false, book: null, search: '', results: [], searching: false, selected: null, confirming: false });
  const dupSearchDebounce = useRef<NodeJS.Timeout | null>(null);

  const openMarkDupModal = (book: Book) => {
    setMarkDupModal({ open: true, book, search: '', results: [], searching: false, selected: null, confirming: false });
  };

  const handleDupSearch = (value: string) => {
    setMarkDupModal((prev) => ({ ...prev, search: value, selected: null }));
    if (dupSearchDebounce.current) clearTimeout(dupSearchDebounce.current);
    if (!value.trim()) {
      setMarkDupModal((prev) => ({ ...prev, results: [] }));
      return;
    }
    dupSearchDebounce.current = setTimeout(async () => {
      setMarkDupModal((prev) => ({ ...prev, searching: true }));
      try {
        const res = await adminApi.get<BooksResponse>('/admin/books', {
          params: { search: value.trim(), filter: 'canonical_only', per_page: 10 },
        });
        setMarkDupModal((prev) => ({ ...prev, results: res.data.books, searching: false }));
      } catch {
        setMarkDupModal((prev) => ({ ...prev, searching: false }));
      }
    }, 400);
  };

  const handleMarkDupConfirm = async () => {
    if (!markDupModal.book || !markDupModal.selected) return;
    setMarkDupModal((prev) => ({ ...prev, confirming: true }));
    try {
      await adminApi.patch(`/admin/books/${markDupModal.book.id}/set-canonical`, {
        canonical_book_id: markDupModal.selected.id,
      });
      addToast('Marked as duplicate.', 'success');
      setDailyRefresh(value => value + 1);
      setMarkDupModal({ open: false, book: null, search: '', results: [], searching: false, selected: null, confirming: false });
      fetchBooks(booksPage, booksSearch, booksFilter);
      fetchQualityReport();
    } catch (err: any) {
      if (err.response?.status === 403) {
        addToast('Admin access required', 'error');
      } else {
        addToast(err.response?.data?.detail || 'Failed to mark as duplicate.', 'error');
      }
      setMarkDupModal((prev) => ({ ...prev, confirming: false }));
    }
  };

  // ── Restore (remove-canonical) ───────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string;
    confirmLabel: string; confirmColor: string;
    action: (() => Promise<void>) | null;
  }>({ open: false, title: '', message: '', confirmLabel: '', confirmColor: '', action: null });
  const [actionLoading, setActionLoading] = useState(false);

  const openConfirm = (
    title: string, message: string, confirmLabel: string, confirmColor: string,
    action: () => Promise<void>
  ) => setConfirmDialog({ open: true, title, message, confirmLabel, confirmColor, action });

  const handleConfirm = async () => {
    if (!confirmDialog.action) return;
    setActionLoading(true);
    await confirmDialog.action();
    setActionLoading(false);
    setConfirmDialog({ open: false, title: '', message: '', confirmLabel: '', confirmColor: '', action: null });
  };

  const handleRestore = (book: Book) => {
    openConfirm(
      'Restore Book',
      `Remove the duplicate link from "${book.title}"? This will mark it as active again.`,
      'Restore',
      'bg-green-600 hover:bg-green-700',
      async () => {
        try {
          await adminApi.patch(`/admin/books/${book.id}/remove-canonical`);
          addToast('Book restored successfully.', 'success');
          setDailyRefresh(value => value + 1);
          fetchBooks(booksPage, booksSearch, booksFilter);
          fetchQualityReport();
        } catch (err: any) {
          if (err.response?.status === 403) {
            addToast('Admin access required', 'error');
          } else {
            addToast(err.response?.data?.detail || 'Failed to restore book.', 'error');
          }
        }
      }
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // TAB 2: Duplicates Scanner
  // ─────────────────────────────────────────────────────────────────
  const [scanResult, setScanResult] = useState<DuplicateScanResult | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [inlineMarkLoading, setInlineMarkLoading] = useState<Record<string, boolean>>({});

  const runScan = useCallback(async () => {
    setScanLoading(true);
    try {
      const res = await adminApi.get<DuplicateScanResult>('/admin/books/duplicates/scan');
      setScanResult(res.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        addToast('Admin access required', 'error');
      } else {
        addToast('Scan failed. Please try again.', 'error');
      }
    } finally {
      setScanLoading(false);
    }
  }, [addToast]);

  const handleInlineMark = async (duplicateBookId: string, canonicalBookId: string) => {
    setInlineMarkLoading((prev) => ({ ...prev, [duplicateBookId]: true }));
    try {
      await adminApi.patch(`/admin/books/${duplicateBookId}/set-canonical`, { canonical_book_id: canonicalBookId });
      setDailyRefresh(value => value + 1);
      addToast('Marked as duplicate.', 'success');
      runScan();
      fetchQualityReport();
    } catch (err: any) {
      if (err.response?.status === 403) {
        addToast('Admin access required', 'error');
      } else {
        addToast(err.response?.data?.detail || 'Failed to mark as duplicate.', 'error');
      }
    } finally {
      setInlineMarkLoading((prev) => ({ ...prev, [duplicateBookId]: false }));
    }
  };

  // ── Duplicate group renderer (reused for title + isbn) ────────────
  const renderDuplicateGroup = (group: DuplicateGroup, gi: number) => {
    const sorted = [...group.books].sort((a, b) => (b.library_count ?? 0) - (a.library_count ?? 0));
    const top = sorted[0];
    return (
      <div key={gi} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="mb-3">
          <p className="font-semibold text-gray-800">{group.title}</p>
          {group.author && <p className="text-xs text-gray-500">{group.author}</p>}
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {sorted.map((book, bi) => {
            const isTop = bi === 0;
            return (
              <div
                key={book.id}
                className={`shrink-0 flex flex-col items-center gap-2 p-3 rounded-xl border ${
                  isTop ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                }`}
                style={{ minWidth: 140 }}
              >
                <CoverThumb url={book.cover_url} title={book.title} />
                <p className="text-xs font-semibold text-gray-700 text-center line-clamp-2 max-w-[120px]">
                  {book.title}
                </p>
                <p className="text-xs text-gray-500">{book.author || '—'}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  isTop ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {book.library_count ?? 0} saves
                </span>
                {isTop ? (
                  <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    ✓ Canonical
                  </span>
                ) : (
                  <button
                    onClick={() => handleInlineMark(book.id, top.id)}
                    disabled={inlineMarkLoading[book.id]}
                    className="text-[11px] font-semibold px-3 py-1 rounded-lg
                      bg-amber/10 text-amber border border-amber/20
                      hover:bg-amber/20 transition-colors active:scale-95 disabled:opacity-50 text-center"
                  >
                    {inlineMarkLoading[book.id] ? 'Marking...' : `Mark dup of\n"${top.title.slice(0, 18)}…"`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  const tabs: { id: TabId; label: string }[] = [
    { id: 'all', label: 'All Books' },
    { id: 'duplicates', label: 'Duplicates Scanner' },
    { id: 'quality', label: 'Quality Report' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Books Management</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Manage the book catalogue, detect duplicates, and monitor quality.
        </p>
      </div>

      <DailyBookCounts refreshKey={dailyRefresh} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => switchTab(tab.id)}
            className={`
              px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-200
              ${activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── TAB 1: All Books ──────────────────────────────────── */}
      {(activeTab === 'all' || activeTab === 'quality') && qualityError && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{qualityError}</p>
          <button type="button" onClick={fetchQualityReport} disabled={qualityLoading}
            className="mt-2 rounded-lg border border-red-200 px-3 py-1.5 font-semibold disabled:opacity-50">
            Retry report
          </button>
        </div>
      )}
      {activeTab === 'all' && (
        <div className="space-y-4">
          {/* Top row: search + filter + refresh */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                id="books-search"
                type="text"
                placeholder="Search title, author or translator..."
                value={booksSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white
                  text-sm text-gray-800 placeholder-gray-400
                  focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  shadow-sm transition-all duration-200"
              />
              {booksSearch && (
                <button
                  onClick={() => { setBooksSearch(''); setBooksPage(1); fetchBooks(1, '', booksFilter); }}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter dropdown */}
            <select
              id="books-filter"
              value={booksFilter}
              onChange={(e) => handleFilterChange(e.target.value as FilterValue)}
              className="py-3 pl-4 pr-8 rounded-xl border border-gray-200 bg-white text-sm text-gray-700
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm"
            >
              <option value="all">All</option>
              <option value="inactive">Inactive</option>
              <option value="no_cover">No Cover</option>
              <option value="no_isbn">Missing ISBN</option>
              <option value="no_author">No Author</option>
              <option value="orphan">Orphan Books</option>
              <option value="duplicates_only">Marked Duplicate</option>
              <option value="no_price">No Price</option>
              <option value="no_publisher">No Publisher</option>
              <option value="no_description">No Description</option>
              <option value="translation">Translation Books</option>
            </select>

            {/* Refresh */}
            <button
              onClick={() => { fetchBooks(booksPage, booksSearch, booksFilter); fetchQualityReport(); setDailyRefresh(value => value + 1); }}
              className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white
                text-sm font-medium text-gray-600 hover:bg-gray-50 shadow-sm transition-colors active:scale-95"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {/* Stats bar */}
          {qualityReport && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {[
                { label: 'Total', value: qualityReport.total_books, color: 'text-gray-800' },
                { label: 'Active', value: qualityReport.active_books, color: 'text-green-700' },
                { label: 'Inactive', value: qualityReport.inactive_books, color: 'text-amber-700' },
                { label: 'No Cover', value: qualityReport.no_cover, color: 'text-red-600' },
                { label: 'Missing ISBN', value: qualityReport.no_isbn, color: 'text-red-700' },
                { label: 'Orphans', value: qualityReport.orphan_books, color: 'text-purple-700' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 text-center">
                  <p className={`text-xl font-bold ${color}`}>{value.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          )}
          {qualityLoading && !qualityReport && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 text-center animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-1" />
                  <div className="h-3 bg-gray-100 rounded w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" id="books-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    {['Cover', 'Title / Author or Translator', 'ISBN', 'Library Saves', 'Status', 'Actions'].map((col) => (
                      <th
                        key={col}
                        className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {booksLoading ? (
                    Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                  ) : books.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <span className="text-4xl">📚</span>
                          <p className="text-gray-400 font-medium">No books found</p>
                          {booksSearch && (
                            <p className="text-gray-400 text-sm">Try a different search term</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    books.map((book) => (
                      <tr key={book.id} className="hover:bg-gray-50/50 transition-colors duration-150">
                        {/* Cover */}
                        <td className="px-4 py-3.5">
                          <CoverThumb url={book.cover_url} title={book.title} />
                        </td>

                        {/* Title + translator for translations, author otherwise */}
                        <td className="px-4 py-3.5">
                          <p className="text-sm font-semibold text-gray-800 truncate max-w-[260px]">{book.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[260px]">
                            {book.is_translation
                              ? book.translator_display?.trim() || 'Translator not set'
                              : book.author_display || book.author || '—'}
                          </p>
                        </td>

                        {/* ISBN */}
                        <td className="px-4 py-3.5">
                          {book.isbn13 ? (
                            <span className="text-xs font-mono text-gray-700">{book.isbn13}</span>
                          ) : (
                            <span className="text-xs font-semibold text-red-700">Missing</span>
                          )}
                        </td>

                        {/* Library saves */}
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-semibold text-gray-700">
                            {(book.library_count ?? 0).toLocaleString()}
                          </span>
                        </td>

                        {/* Status badge */}
                        <td className="px-4 py-3.5">
                          {book.is_active ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                              Active
                            </span>
                          ) : book.canonical_book_id ? (
                            <span
                              title={`Canonical: ${book.canonical_title || book.canonical_book_id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                              onClick={() => {
                                addToast(`Canonical: ${book.canonical_title || book.canonical_book_id}`, 'error');
                              }}
                            >
                              Duplicate → {(book.canonical_title || '').slice(0, 20) || '…'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">
                              Inactive
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {!book.isbn13 && (
                              <button
                                onClick={() => openRepairModal(book)}
                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                              >
                                Verify ISBN
                              </button>
                            )}
                            {/* Edit */}
                            <button
                              onClick={() => openEditModal(book)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                bg-primary/10 text-primary border border-primary/20
                                hover:bg-primary/20 transition-colors active:scale-95"
                            >
                              Edit
                            </button>

                            {/* View Refs */}
                            <button
                              onClick={() => openRefsModal(book)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                bg-gray-100 text-gray-600 border border-gray-200
                                hover:bg-gray-200 transition-colors active:scale-95"
                            >
                              View Refs
                            </button>

                            {/* Mark as Duplicate (only if active) */}
                            {book.is_active && (
                              <button
                                onClick={() => openMarkDupModal(book)}
                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                  bg-amber/10 text-amber border border-amber/20
                                  hover:bg-amber/20 transition-colors active:scale-95"
                              >
                                Mark Duplicate
                              </button>
                            )}

                            {/* Restore (only if canonical_book_id set) */}
                            {book.canonical_book_id && (
                              <button
                                onClick={() => handleRestore(book)}
                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                  bg-green-50 text-green-700 border border-green-200
                                  hover:bg-green-100 transition-colors active:scale-95"
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {booksTotalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
                <span className="text-sm text-gray-500">
                  Showing <span className="font-semibold text-gray-700">{(booksPage - 1) * perPage + 1}</span>
                  –<span className="font-semibold text-gray-700">{Math.min(booksPage * perPage, booksTotal)}</span>
                  {' '}of <span className="font-semibold text-gray-700">{booksTotal}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setBooksPage((p) => Math.max(1, p - 1))}
                    disabled={booksPage === 1}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                      bg-white border border-gray-200 text-gray-600
                      hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                      transition-colors active:scale-95"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    Prev
                  </button>
                  <div className="flex items-center gap-1 px-3">
                    <span className="text-sm font-bold text-primary">{booksPage}</span>
                    <span className="text-sm text-gray-400">/</span>
                    <span className="text-sm text-gray-500">{booksTotalPages}</span>
                  </div>
                  <button
                    onClick={() => setBooksPage((p) => Math.min(booksTotalPages, p + 1))}
                    disabled={booksPage === booksTotalPages}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                      bg-white border border-gray-200 text-gray-600
                      hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                      transition-colors active:scale-95"
                  >
                    Next
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TAB 2: Duplicates Scanner ─────────────────────────── */}
      {activeTab === 'duplicates' && (
        <div className="space-y-6">
          {/* Scan button */}
          <div className="flex items-center gap-4">
            <button
              onClick={runScan}
              disabled={scanLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white
                bg-primary hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95 shadow-sm"
            >
              {scanLoading ? (
                <>
                  <Spinner />
                  Scanning...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Scan Now
                </>
              )}
            </button>
            {scanResult && (
              <p className="text-sm text-gray-500">
                Found{' '}
                <span className="font-semibold text-gray-800">
                  {(scanResult.title_duplicates?.length ?? 0) + (scanResult.isbn_duplicates?.length ?? 0)}
                </span>{' '}
                duplicate group(s)
              </p>
            )}
          </div>

          {/* No scan yet */}
          {!scanResult && !scanLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="text-5xl mb-4">🔍</span>
              <p className="text-gray-400 font-medium">Click "Scan Now" to detect duplicate books</p>
            </div>
          )}

          {/* Results */}
          {scanResult && (
            <>
              {/* Title duplicates */}
              {(scanResult.title_duplicates?.length ?? 0) > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Title Duplicates ({scanResult.title_duplicates.length})
                  </h3>
                  {scanResult.title_duplicates.map((group, gi) => renderDuplicateGroup(group, gi))}
                </div>
              )}

              {/* ISBN duplicates */}
              {(scanResult.isbn_duplicates?.length ?? 0) > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                    ISBN Duplicates ({scanResult.isbn_duplicates.length})
                  </h3>
                  {scanResult.isbn_duplicates.map((group, gi) => renderDuplicateGroup(group, gi))}
                </div>
              )}

              {/* Empty state */}
              {(scanResult.title_duplicates?.length ?? 0) === 0 && (scanResult.isbn_duplicates?.length ?? 0) === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <span className="text-5xl mb-4">✅</span>
                  <p className="text-gray-400 font-medium">No duplicates found</p>
                  <p className="text-gray-400 text-sm mt-1">Your book catalogue looks clean!</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── TAB 3: Quality Report ─────────────────────────────── */}
      {activeTab === 'quality' && (
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <button
              onClick={fetchQualityReport}
              disabled={qualityLoading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white
                text-sm font-medium text-gray-600 hover:bg-gray-50 shadow-sm transition-colors active:scale-95 disabled:opacity-50"
            >
              {qualityLoading ? (
                <>
                  <Spinner />
                  Refreshing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh Report
                </>
              )}
            </button>
          </div>

          {/* Skeleton */}
          {qualityLoading && !qualityReport && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-4" />
                  <div className="h-8 bg-gray-100 rounded w-full" />
                </div>
              ))}
            </div>
          )}

          {/* 8 stat cards */}
          {qualityReport && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Books', value: qualityReport.total_books, color: 'text-gray-800', icon: '📚', filter: null as FilterValue | null },
                { label: 'Active', value: qualityReport.active_books, color: 'text-green-700', icon: '✅', filter: null as FilterValue | null },
                { label: 'Inactive / Duplicate', value: qualityReport.inactive_books, color: 'text-amber-700', icon: '⏸️', filter: 'inactive' as FilterValue },
                { label: 'No Cover', value: qualityReport.no_cover, color: 'text-red-600', icon: '🖼️', filter: 'no_cover' as FilterValue },
                { label: 'Missing ISBN', value: qualityReport.no_isbn, color: 'text-red-700', icon: '🔢', filter: 'no_isbn' as FilterValue },
                { label: 'No Author', value: qualityReport.no_author, color: 'text-orange-600', icon: '✍️', filter: 'no_author' as FilterValue },
                { label: 'Orphan Books', value: qualityReport.orphan_books, color: 'text-purple-700', icon: '🔗', filter: 'orphan' as FilterValue },
                { label: 'Marked Duplicate', value: qualityReport.marked_as_duplicate, color: 'text-red-700', icon: '🔁', filter: 'duplicates_only' as FilterValue },
                { label: 'Duplicate Groups', value: qualityReport.duplicate_groups, color: 'text-indigo-700', icon: '📦', filter: null as FilterValue | null },
                { label: 'No Price', value: qualityReport.no_price, color: 'text-yellow-600', icon: '💰', filter: 'no_price' as FilterValue },
                { label: 'No Publisher', value: qualityReport.no_publisher, color: 'text-blue-600', icon: '🏢', filter: 'no_publisher' as FilterValue },
                { label: 'No Description', value: qualityReport.no_description, color: 'text-teal-600', icon: '📝', filter: 'no_description' as FilterValue },
                { label: 'Translations', value: qualityReport.translation_count, color: 'text-fuchsia-600', icon: '🌐', filter: 'translation' as FilterValue },
              ].map(({ label, value, color, icon, filter }) => (
                <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xl">{icon}</span>
                  </div>
                  <p className={`text-3xl font-bold ${color}`}>{value.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                  {filter && (
                    <button
                      onClick={() => {
                        setActiveTab('all');
                        setBooksFilter(filter);
                        setBooksPage(1);
                        fetchBooks(1, booksSearch, filter);
                      }}
                      className="mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                        bg-primary/10 text-primary border border-primary/20
                        hover:bg-primary/20 transition-colors active:scale-95 self-start"
                    >
                      View →
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────── */}
      {/* Modals                                                      */}
      {/* ─────────────────────────────────────────────────────────── */}

      {repairBook && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Verify missing ISBN</h3>
                <p className="text-sm text-gray-600 mt-1">{repairBook.title} · {repairBook.author || 'Unknown author'}</p>
                <p className="text-xs font-mono text-gray-500 mt-1">Book ID: {repairBook.id}</p>
              </div>
              <button onClick={() => setRepairBook(null)} disabled={repairLoading} className="text-gray-500 hover:text-gray-800">Close</button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-700">Existing library saves: {repairPreview?.library_count ?? repairBook.library_count}. The ISBN will be attached to this same book.</p>
              <section aria-label="Saved ISBN numbers" className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                <p className="font-semibold">Saved ISBN numbers</p>
                {repairSaved.status === 'loading' ? <p role="status" className="mt-1">Loading saved ISBN numbers…</p>
                  : repairSaved.status === 'error' ? <div className="mt-1">
                    <p role="alert">Saved ISBN numbers could not be loaded.</p>
                    <button onClick={() => setRepairSavedRetry(value => value + 1)} className="mt-2 underline">Retry saved ISBN lookup</button>
                  </div>
                  : repairSaved.items.length === 0 ? <p className="mt-1">No saved ISBN number found in this book's existing records.</p>
                  : <>
                    <ul className="mt-2 space-y-2">
                      {repairSaved.items.map(item => <li key={item.source}>
                        <p>{savedIsbnSourceLabels[item.source]}: <span className="font-mono break-all">{item.isbn}</span></p>
                        <button onClick={() => changeRepairIsbn(item.isbn)} disabled={repairLoading}
                          className="mt-1 underline disabled:opacity-50">Use this ISBN</button>
                      </li>)}
                    </ul>
                    <p className="mt-2">Check the saved number against the physical book before attaching.</p>
                  </>}
              </section>
              {repairPreview?.linked_sinhala_book && (
                <p className="text-sm text-blue-900">Linked Sinhala book: {repairPreview.linked_sinhala_book.title}</p>
              )}
              {repairPreview?.matching_sinhala_book && (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                  ISBN matches a Sinhala catalog row: {repairPreview.matching_sinhala_book.title}. Compare the physical edition before confirming.
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700" htmlFor="repair-isbn">ISBN printed on the physical book</label>
              <input
                id="repair-isbn"
                value={repairIsbn}
                onChange={(e) => changeRepairIsbn(e.target.value)}
                placeholder="978... or ISBN-10"
                maxLength={32}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm"
              />
              <button onClick={checkRepairIsbn} disabled={repairLoading || !repairIsbn.trim()} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-800 disabled:opacity-50">
                Check ISBN
              </button>
              {repairPreview && repairIsbn.trim() && (
                <div className={`rounded-lg p-3 text-sm ${repairPreview.can_apply ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {repairPreview.can_apply ? `ISBN checked: ${repairPreview.candidate_isbn13}. No catalog conflict found.` : repairPreview.reason}
                  {repairPreview.can_apply && repairPreview.checksum_valid === false && (
                    <p className="mt-2 rounded bg-amber-50 p-2 text-amber-900" role="note">
                      The checksum does not match. Confirm the number exactly as printed on this edition. It will be saved unchanged; no replacement number will be generated.
                    </p>
                  )}
                  {repairPreview.conflict_book && <p className="mt-1">Conflict: {repairPreview.conflict_book.title} ({repairPreview.conflict_book.id})</p>}
                </div>
              )}
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={repairVerified} onChange={(e) => setRepairVerified(e.target.checked)} disabled={!repairPreview?.can_apply} className="mt-1" />
                I checked the ISBN on the physical book and confirmed this is the same edition.
              </label>
              <label className="block text-sm font-medium text-gray-700" htmlFor="repair-note">Review note (optional)</label>
              <textarea id="repair-note" value={repairNote} onChange={(e) => setRepairNote(e.target.value)} maxLength={500} rows={2} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Edition or barcode details" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setRepairBook(null)} disabled={repairLoading} className="rounded-lg px-4 py-2 text-sm text-gray-600">Cancel</button>
                <button onClick={applyRepairIsbn} disabled={repairLoading || !repairPreview?.can_apply || !repairVerified} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {repairLoading ? 'Saving...' : 'Attach ISBN'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {editModal.open && editModal.book && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">✏️ Edit Book</h3>
              <button
                onClick={closeEditModal}
                aria-label="Close book editor"
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editModal.loading ? (
              <div className="flex justify-center py-16">
                <Spinner size="h-8 w-8" />
              </div>
            ) : editModal.error ? (
              <div className="space-y-4">
                <p role="alert" className="text-sm text-red-700">{editModal.error}</p>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={closeEditModal} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                  <button type="button" onClick={() => openEditModal(editModal.book!)}
                    className="rounded-xl border px-4 py-2 text-sm font-semibold">Retry</button>
                  <button type="button" disabled
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white opacity-50 cursor-not-allowed">Save Changes</button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="relative shrink-0">
                      {editModal.book.cover_url ? (
                        <img
                          src={editModal.book.cover_url}
                          alt="Cover preview"
                          className="w-20 h-32 object-cover rounded-md shadow-sm border border-gray-200"
                        />
                      ) : (
                        <div className="w-20 h-32 rounded-md bg-gray-100 border border-gray-200 flex flex-col items-center justify-center text-gray-400">
                          <span className="text-2xl mb-1">📖</span>
                          <span className="text-[10px]">No Cover</span>
                        </div>
                      )}
                      {coverUploadLoading && (
                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-md">
                          <Spinner size="h-6 w-6" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 pt-2">
                      <label className="text-sm font-medium text-gray-700">Cover Image</label>
                      <button
                        type="button"
                        onClick={() => document.getElementById('cover-upload-input')?.click()}
                        disabled={coverUploadLoading}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50 self-start"
                      >
                        Change Cover
                      </button>
                      <input
                        id="cover-upload-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleCoverUpload}
                      />
                      <p className="text-xs text-gray-400">Uploads to Cloudflare R2.</p>
                    </div>
                  </div>

                  {[
                    { key: 'title', label: 'Title', type: 'text' },
                    { key: 'english_name', label: 'English Name', type: 'text' },
                    { key: 'page_count', label: 'Page Count', type: 'number' },
                    { key: 'genre', label: 'Genre', type: 'text' },
                    { key: 'language', label: 'Language', type: 'text' },
                    { key: 'price_lkr', label: 'Price (LKR)', type: 'number' },
                  ].map(({ key, label, type }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      <input
                        type={type}
                        value={(editForm as any)[key] ?? ''}
                        onChange={(e) => setEditForm((prev) => ({
                          ...prev,
                          [key]: type === 'number'
                            ? (e.target.value === '' ? undefined : Number(e.target.value))
                            : e.target.value,
                        }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2
                          text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  ))}
                  <CanonicalPicker key={`${editModal.book?.id}:author:${!!editModal.book?.is_translation}`} kind="author"
                    initialName={editModal.book?.is_translation ? translationForm.original_author : editForm.author || ''}
                    initialId={(editModal.book?.is_translation ? translationForm.author_id
                      : Object.prototype.hasOwnProperty.call(editForm, 'author_ref') ? editForm.author_ref?.id : editModal.book?.author_id) || undefined}
                    onSelect={(id, name) => editModal.book?.is_translation ? selectOriginalAuthor(id, name)
                      : setEditForm(prev => ({ ...prev, author: name, author_ref: id ? { id, role: 'author' } : undefined }))} />
                  <CanonicalPicker key={`${editModal.book?.id}:publisher`} kind="publisher" initialName={editForm.publisher || ''}
                    initialId={(Object.prototype.hasOwnProperty.call(editForm, 'publisher_ref') ? editForm.publisher_ref?.id : editModal.book?.publisher_id) || undefined}
                    onSelect={(id, name) => setEditForm(prev => ({ ...prev, publisher: name, publisher_ref: id ? { id } : undefined }))} />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editForm.description ?? ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2
                        text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>
                </div>

                {/* ── 🌐 Translation Details (collapsible) ───────── */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Collapsible header */}
                  <button
                    type="button"
                    onClick={() => setTranslationSectionOpen((o) => !o)}
                    aria-expanded={translationSectionOpen}
                    aria-controls="book-translation-details"
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      🌐 Translation Details
                      {translationForm.is_translation && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700">ON</span>
                      )}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${translationSectionOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="px-4 pb-3 bg-gray-50">
                    <CanonicalPicker key={`${editModal.book?.id}:translation-author:${translationForm.is_translation}`}
                      kind="author" initialName={translationForm.original_author} initialId={translationForm.author_id || undefined}
                      onSelect={selectOriginalAuthor} />
                  </div>

                  {translationSectionOpen && (
                    <div id="book-translation-details" className="px-4 py-4 space-y-4">

                      {/* Is Translation toggle */}
                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={translationForm.is_translation}
                          onChange={(e) =>
                            setTranslationForm((prev) => ({ ...prev, is_translation: e.target.checked, author_id: null }))
                          }
                          className="w-4 h-4 rounded border-gray-300 accent-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">Is this a translation?</span>
                      </label>

                      {translationForm.is_translation && (
                        <>
                          {/* Original Title */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Original Title</label>
                            <input
                              type="text"
                              value={translationForm.original_title}
                              onChange={(e) =>
                                setTranslationForm((prev) => ({ ...prev, original_title: e.target.value }))
                              }
                              placeholder="Title in original language…"
                              className="w-full border border-gray-200 rounded-xl px-4 py-2
                                text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                          </div>

                          {/* Translator — author search (translators are authors with is_translator=true) */}
                          <AuthorSearchInput
                            label="Translator"
                            value={translationForm.translator}
                            linkedId={translationForm.translator_id}
                            placeholder="Search or type translator name…"
                            onChange={(text) =>
                              setTranslationForm((prev) => ({ ...prev, translator: text, translator_id: null }))
                            }
                            onSelect={(id, name) =>
                              setTranslationForm((prev) => ({ ...prev, translator: name, translator_id: id }))
                            }
                            onClear={() =>
                              setTranslationForm((prev) => ({ ...prev, translator: '', translator_id: null }))
                            }
                          />
                        </>
                      )}

                      {/* Separate Save button — never shares the general save handler */}
                      <button
                        type="button"
                        onClick={handleTranslationSave}
                        disabled={translationSaving}
                        className="w-full py-2.5 rounded-xl text-sm font-bold text-white
                          bg-fuchsia-600 hover:bg-fuchsia-700
                          disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {translationSaving ? 'Saving…' : '🌐 Save Translation Details'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-5">
                  <button
                    onClick={closeEditModal}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200
                      text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditSave}
                    disabled={editLoading}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white
                      bg-primary hover:bg-primary/90
                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* View Refs Modal */}
      {refsModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">📎 References</h3>
              <button
                onClick={() => setRefsModal((prev) => ({ ...prev, open: false }))}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4 font-medium truncate">{refsModal.bookTitle}</p>
            {refsModal.loading ? (
              <div className="flex justify-center py-8">
                <Spinner size="h-8 w-8" />
              </div>
            ) : refsModal.data ? (
              <div className="space-y-3">
                {[
                  { label: 'Library Saves', value: refsModal.data.library_count, icon: '📚' },
                  { label: 'Wisdom Posts', value: refsModal.data.wisdom_posts_count, icon: '💡' },
                  { label: 'Price History Entries', value: refsModal.data.price_history_count, icon: '💰' },
                  { label: 'Series Books', value: refsModal.data.series_books_count, icon: '📖' },
                ].map(({ label, value, icon }) => (
                  <div key={label} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 border border-gray-100">
                    <span className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{icon}</span>
                      {label}
                    </span>
                    <span className="text-sm font-bold text-gray-800">{value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : null}
            <button
              onClick={() => setRefsModal((prev) => ({ ...prev, open: false }))}
              className="w-full mt-5 py-2.5 rounded-xl border border-gray-200
                text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Mark as Duplicate Modal */}
      {markDupModal.open && markDupModal.book && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">🔁 Mark as Duplicate</h3>
              <button
                onClick={() => setMarkDupModal((prev) => ({ ...prev, open: false }))}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              Marking: <span className="font-semibold text-gray-800">"{markDupModal.book.title}"</span>
            </p>

            {/* Warning */}
            <div className="bg-amber/10 border border-amber/20 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs text-amber-800 leading-relaxed">
                ⚠️ This book will be hidden from the app. All user library entries, wisdom posts, and price history
                stay untouched — they'll automatically show the canonical book's data instead.
              </p>
            </div>

            {/* Search canonical */}
            <label className="block text-sm font-medium text-gray-700 mb-1">Search canonical book</label>
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Type to search active books..."
                value={markDupModal.search}
                onChange={(e) => handleDupSearch(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2
                  text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              {markDupModal.searching && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <Spinner size="h-4 w-4" />
                </div>
              )}
            </div>

            {/* Search results */}
            {markDupModal.results.length > 0 && !markDupModal.selected && (
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-3 max-h-48 overflow-y-auto">
                {markDupModal.results.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setMarkDupModal((prev) => ({ ...prev, selected: b, results: [] }))}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50
                      border-b border-gray-100 last:border-b-0 transition-colors text-left"
                  >
                    <CoverThumb url={b.cover_url} title={b.title} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{b.title}</p>
                      <p className="text-xs text-gray-500 truncate">{b.author || '—'}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Selected canonical */}
            {markDupModal.selected && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-green-300 bg-green-50 mb-3">
                <CoverThumb url={markDupModal.selected.cover_url} title={markDupModal.selected.title} />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-green-700 mb-0.5">Selected canonical:</p>
                  <p className="text-sm font-semibold text-gray-800 truncate">{markDupModal.selected.title}</p>
                  <p className="text-xs text-gray-500 truncate">{markDupModal.selected.author || '—'}</p>
                </div>
                <button
                  onClick={() => setMarkDupModal((prev) => ({ ...prev, selected: null, search: '' }))}
                  className="ml-auto text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <div className="flex gap-3 mt-2">
              <button
                onClick={() => setMarkDupModal((prev) => ({ ...prev, open: false }))}
                className="flex-1 py-2.5 rounded-xl border border-gray-200
                  text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkDupConfirm}
                disabled={!markDupModal.selected || markDupModal.confirming}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white
                  bg-amber-600 hover:bg-amber-700
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {markDupModal.confirming ? 'Confirming...' : 'Confirm Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmColor={confirmDialog.confirmColor}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={actionLoading}
      />

      {/* Toasts */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default BooksPage;
